import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../util/errors';
import { MARKETPLACE_FILTER } from '../util/catalogueCore';

/**
 * categoryCounts — live listing counts per landing-page category tile.
 *
 * Fires N parallel sdk.listings.query calls with perPage: 1, reading
 * meta.totalItems from each response so we don't download the listing
 * payload itself. Cached by a `key` string derived from the category
 * params; subsequent dispatches return early if the count is already
 * loaded, so re-navigating to the landing doesn't re-fire the fan-out.
 *
 * Why Redux (vs. local component state): SSR hydration, dedup across
 * remount, and consistency with the rest of this template's data layer.
 */

// Stable key for the counts map — mirrors how the SDK serializes the
// params so two callers requesting the same filter hit the same cache.
const keyFromParams = (params = {}) =>
  Object.keys(params)
    .sort()
    .map(k => `${k}=${params[k]}`)
    .join('&');

const fetchCategoryCountsPayloadCreator = async (arg, thunkAPI) => {
  const { extra: sdk, rejectWithValue, getState } = thunkAPI;
  const { categories } = arg;

  const existing = getState().categoryCounts?.counts || {};

  // Only fetch categories that aren't already cached — dispatches from
  // re-renders shouldn't re-fire the network.
  const toFetch = categories.filter(c => existing[keyFromParams(c.params)] == null);
  if (toFetch.length === 0) {
    return { counts: {} };
  }

  try {
    const results = await Promise.all(
      toFetch.map(c =>
        sdk.listings
          .query({
            ...c.params,
            // P25009-410 — a tile counts ads; catalogue rows are not on the marketplace.
            ...MARKETPLACE_FILTER,
            perPage: 1,
            page: 1,
            'fields.listing': ['id'],
          })
          .then(res => ({
            key: keyFromParams(c.params),
            total: res?.data?.meta?.totalItems ?? 0,
          }))
          .catch(() => ({
            key: keyFromParams(c.params),
            total: null, // keep null so the UI knows to fall back
          }))
      )
    );

    const counts = results.reduce((acc, { key, total }) => {
      acc[key] = total;
      return acc;
    }, {});

    return { counts };
  } catch (error) {
    return rejectWithValue(storableError(error));
  }
};

export const fetchCategoryCounts = createAsyncThunk(
  'categoryCounts/fetch',
  fetchCategoryCountsPayloadCreator
);

const categoryCountsSlice = createSlice({
  name: 'categoryCounts',
  initialState: {
    counts: {}, // { [keyFromParams(params)]: number | null }
    inProgress: false,
    error: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchCategoryCounts.pending, state => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(fetchCategoryCounts.fulfilled, (state, action) => {
        state.inProgress = false;
        Object.assign(state.counts, action.payload.counts || {});
      })
      .addCase(fetchCategoryCounts.rejected, (state, action) => {
        state.inProgress = false;
        state.error = action.payload || null;
      });
  },
});

// Selector: given a category { id, params }, pull the cached count (or null).
export const selectCategoryCount = category => state =>
  state.categoryCounts?.counts?.[keyFromParams(category?.params || {})] ?? null;

export { keyFromParams };
export default categoryCountsSlice.reducer;
